<template><div><h1 id="unbricking-flashing-with-a-ch341a-usb-programmer" tabindex="-1"><a class="header-anchor" href="#unbricking-flashing-with-a-ch341a-usb-programmer"><span>Unbricking/Flashing with a ch341a USB Programmer</span></a></h1>
<h2 id="requirements" tabindex="-1"><a class="header-anchor" href="#requirements"><span>Requirements</span></a></h2>
<div class="hint-container tip">
<p class="hint-container-title">Tips</p>
<p>Most Skylake and older models (with a few exceptions) use a SOIC-8 flash chip which is easily clippable. Most if not all Kabylake/Apollolake and newer devices use a WSON-8 flash chip which can't be clipped, instead you need a WSON-8 probe. Check the part number of your flash chip to find the correct size needed.</p>
</div>
<ol>
<li>
<p>A ChromeOS device</p>
</li>
<li>
<p>A device running Linux from which to run flashrom. For this guide, I will use a Ubuntu live USB.</p>
</li>
<li>
<p>A ch341a USB flash programmer</p>
</li>
<li>
<p>A 1.8v adapter</p>
<div class="hint-container tip">
<p class="hint-container-title">Tips</p>
<p>The adapter is required for devices which use 1.8v flash chips. Some/Most Baytrail, Braswell, Skylake and many newer devices use a 1.8v flash chip. Baytrail is more reliable flashing at 3.3v though due to current leakage</p>
</div>
</li>
<li>
<p>Either a SOIC-8 chip clip or a WSON-8 probe</p>
</li>
</ol>
<p>A ch341a programmer, 1.8v adapter, and a SOIC-8 clip  are often bundled together at a lower cost, and if you're unsure if your device uses a 1.8v flash chip or a 3.3v one, it makes sense to have the adapter on hand if needed. You can look up the part number of your flash chip to determine which voltage it needs.</p>
<h2 id="hardware-disassembly" tabindex="-1"><a class="header-anchor" href="#hardware-disassembly"><span>Hardware Disassembly</span></a></h2>
<p>While this is somewhat device-specific, the main points are the same:</p>
<ul>
<li>Disconnect all external power</li>
<li>Remove bottom cover (screws are often located under rubber feet or strips)
<ul>
<li>Some Chromebooks open up through the back and some through the keyboard; see <RouteLink to="/docs/firmware/wp/disabling.html#disconnecting-the-battery">Disconnecting the Battery</RouteLink>. On keyboard-open boards you have to pry the keyboard and disconnect a ribbon.</li>
</ul>
</li>
<li>Disconnect the internal battery (for Chromeboxes, disconnect the small CMOS battery)</li>
<li>Locate the SPI flash chip</li>
</ul>
<div class="hint-container caution">
<p class="hint-container-title">Caution</p>
<p>Most ChromeOS devices use a Winbond flash chip, though some use a compatible chip from another manufacturer, eg Gigadevices. It will be either an 8MB, 16MB, or 32MB chip, with the identifier W25Q64[xx] (8MB),  W25Q128[xx] (16MB), or W25Q256[xx] (32MB) where [xx] is usually FV or DV. We do <strong>not</strong> want to touch the EC firmware chip, which is identified by W25X40[xx].</p>
</div>
<div class="hint-container warning">
<p class="hint-container-title">Warning</p>
<p>Unfortunately, many devices have the flash chip located on the top side of the main board, and require fully removing the main board in order to flash.</p>
</div>
<div class="hint-container tip">
<p class="hint-container-title">Tips</p>
<p>Pin 1 of the flash chip will be notated by a dot/depression on the chip; be sure to align this with pin 1 on the chip clip wiring.</p>
</div>
<p>Googling should locate a disassembly guide for most models. If you can't find one for your exact model, try to find one for another model of the same manufacturer as the bottom cover (or keyboard) removal tends to be very similar.</p>
<h2 id="prepping-to-flash" tabindex="-1"><a class="header-anchor" href="#prepping-to-flash"><span>Prepping to Flash</span></a></h2>
<p>Once you have your device disassembled and flash chip located, boot up the flashing environment. Most any Linux setup should do as long as either flashrom is available from the distro's software repositories, or it's 64-bit x86 (in which case you can download a statically compiled build of flashrom from mrchromebox.tech). This guide will use a Ubuntu live session booted from USB.</p>
<p>So let's get to it:</p>
<ol>
<li>
<p>Boot your Linux environment (Ubuntu live USB or later recommended)</p>
</li>
<li>
<p>Connect to WiFi/internet</p>
</li>
<li>
<p>Open a (non-root) terminal/shell window, change to home directory</p>
<ul>
<li><code v-pre>cd;</code></li>
</ul>
</li>
<li>
<p>Install flashrom via apt:</p>
<ul>
<li><code v-pre>sudo apt update</code></li>
<li><code v-pre>sudo apt install flashrom</code></li>
</ul>
</li>
<li>
<p>Assemble ch341a programmer, 1.8v adapter (if needed), and chip clip/wiring. Ensure that pin 1 is correct and consistent.
<img src="/ch341aunbrick/500px-Ch341a_annotated.png" alt="image"></p>
</li>
<li>
<p>Connect the chip clip to the SPI flash chip, or get ready to hold down your WSON-8 probe, rubber bands can be used to hold it down while flashing, then connect the CH341a to the Linux host machine. Note the dot/depression indicating pin 1.
<img src="/ch341aunbrick/500px-SOIC-8_chip.jpg" alt="image"></p>
</li>
<li>
<p>Test connectivity and ensure the flash chip is properly identified:</p>
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi</code></li>
</ul>
<p>Flashrom will produce output identifying the flash chip. If it doesn't, double check your connections to the programmer and the chip clip and retry.</p>
<p><img src="/ch341aunbrick/500px-Flashrom_chip_detect.png" alt="image"></p>
</li>
<li>
<p>Determine file to be flashed</p>
<ul>
<li>
<p>Depending on your desired use for the device, you have 3 options for flashing:</p>
<p><strong>Option 1: The backup file of the stock firmware created by my Firmware Utility Script</strong></p>
<ul>
<li>If using this, simply copy the file from USB into the home directory of the live USB user</li>
</ul>
<p><strong>Option 2: The UEFI Full ROM firmware for the device</strong></p>
<ul>
<li>If you were flashing the UEFI firmware when things went sideways, then that's the easiest way to proceed. Use <a href="https://github.com/MrChromebox/scripts/blob/main/download-fullrom.sh" target="_blank" rel="noopener noreferrer"><code v-pre>download-fullrom.sh</code></a> to fetch the correct image from the CDN (resolves board slugs and HWIDs via <code v-pre>device-db.sh</code>):
<ul>
<li><code v-pre>wget https://raw.githubusercontent.com/MrChromebox/scripts/main/download-fullrom.sh</code></li>
<li><code v-pre>chmod +x download-fullrom.sh</code></li>
<li><code v-pre>./download-fullrom.sh &lt;board or HWID&gt;</code></li>
<li>Examples for the Acer Chromebook 14 CB3-431 (EDGAR):
<ul>
<li><code v-pre>./download-fullrom.sh edgar</code></li>
<li><code v-pre>./download-fullrom.sh EDGAR</code></li>
<li><code v-pre>./download-fullrom.sh -n edgar</code> (dry-run: print the URL only)</li>
</ul>
</li>
<li>Pin a specific release train if needed: <code v-pre>./download-fullrom.sh 2606.1 edgar</code></li>
<li>The <code v-pre>.rom</code> is written under <code v-pre>./MrChromebox-&lt;version&gt;/</code> by default (<code v-pre>-o DIR</code> to choose another). Run <code v-pre>./download-fullrom.sh -h</code> for all options.</li>
</ul>
</li>
</ul>
<p><strong>Option 3: The shellball firmware for the device</strong></p>
<ul>
<li>A shellball image is a generic firmware image embedded in a ChromeOS recovery image. It doesn't contain any unique data for your device (serial, HWID, MAC address, etc) and the GBB flags are set to force Developer Mode on, among other things.</li>
<li>This is only used if you want to go back to stock, but you lost your backup (or never made one). It requires a large amount of disk space (5GB+) as it will download a full ChromeOS recovery image for your device and extract the firmware from it.</li>
<li>A shellball ROM can be downloaded via the coreboot <code v-pre>crosfirmware.sh</code> script from a Linux terminal:
<ul>
<li><code v-pre>wget https://github.com/coreboot/coreboot/raw/refs/heads/main/util/chromeos/crosfirmware.sh</code></li>
<li><code v-pre>bash crosfirmware.sh &lt;board name in all lowercase&gt;</code></li>
<li>Example for the Acer Chromebook 14 CB3-431 (EDGAR):
<ul>
<li><code v-pre>wget https://github.com/coreboot/coreboot/raw/refs/heads/main/util/chromeos/crosfirmware.sh</code></li>
<li><code v-pre>bash crosfirmware.sh edgar</code></li>
</ul>
</li>
<li>This will produce a shellball firmware image named <code v-pre>coreboot-Google_Edgar.&lt;version&gt;.bin</code>. It will not have a valid HWID and will need the GBB flags reset after flashing to the device.</li>
</ul>
</li>
</ul>
</li>
</ul>
</li>
</ol>
<h3 id="persisting-the-board-s-vital-product-data-vpd-and-hardware-id-hwid" tabindex="-1"><a class="header-anchor" href="#persisting-the-board-s-vital-product-data-vpd-and-hardware-id-hwid"><span>Persisting the board's Vital Product Data (VPD) and Hardware ID (HWID)</span></a></h3>
<p>The firmware in all ChromeOS devices contains a section (RO_VPD) which stores board-specific data, like the serial number, localization settings, and on many devices which have an Ethernet port, the LAN MAC address as well. When flashing via the Firmware Utility Script, the script will automatically extract this from the running firmware and inject it into the firmware to be flashed, so the device serial, LAN MAC address, etc are all maintained. Without this, the device will use a default/generic LAN MAC address set by coreboot. While not ideal, this is only really an issue if two or more of the same device are on the same LAN segment (or you're statically assigning IP addresses based on MAC). But for completeness, if flashing the UEFI firmware or shellball ROM, we'll extract the VPD (either from the board itself or a backup made by the script) and inject it into the firmware to be flashed.</p>
<div class="hint-container tip">
<p class="hint-container-title">NOTE</p>
<p>You don't need to do this if flashing a stock firmware backup created by the Firmware Utility Script; that image already contains the VPD.</p>
</div>
<ol>
<li>For both the options below, we'll need to use the gbb_utility and cbfstool (coreboot filesystem) binaries, so let's download/extract those:
<ul>
<li><code v-pre>wget https://mrchromebox.tech/files/util/cbfstool.tar.gz &amp;&amp; tar -zxf cbfstool.tar.gz</code></li>
<li><code v-pre>wget https://mrchromebox.tech/files/util/gbb_utility.tar.gz &amp;&amp; tar -zxf gbb_utility.tar.gz</code></li>
<li>Option 1: Extract VPD and HWID from the firmware on device
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi -r badflash.rom</code></li>
<li><code v-pre>./cbfstool badflash.rom read -r RO_VPD -f vpd.bin</code></li>
<li><code v-pre>./gbb_utility badflash.rom --get --hwid | sed 's/^hardware_id: //' &gt; hwid.txt</code></li>
</ul>
</li>
<li>Option 2: Extract VPD and HWID from stock firmware backup created by Firmware Utility Script (this assumes the file has been copied into working directory)
<ul>
<li><code v-pre>./cbfstool stock-firmware-&lt;devicename&gt;-&lt;date&gt;.rom read -r RO_VPD -f vpd.bin</code></li>
<li><code v-pre>./gbb_utility stock-firmware-&lt;devicename&gt;-&lt;date&gt;.rom --get --hwid | sed 's/^hardware_id: //' &gt; hwid.txt</code></li>
</ul>
</li>
</ul>
</li>
<li>Then we inject the VPD and HWID into the firmware image to be flashed.
<ul>
<li><code v-pre>./cbfstool &lt;Shellball ROM/UEFI Full ROM filename&gt; write -r RO_VPD -f vpd.bin</code></li>
<li>For UEFI Full ROM run
<ul>
<li><code v-pre>./cbfstool &lt;UEFI Full ROM filename&gt; add -n hwid -f hwid.txt -t raw</code></li>
</ul>
</li>
<li>For Shellball run
<ul>
<li><code v-pre>./gbb_utility &lt;Shellball ROM&gt; --set --hwid=&quot;$(cat hwid.txt)&quot;</code></li>
</ul>
</li>
</ul>
</li>
</ol>
<p>Now the firmware image is ready to be flashed, and will maintain the device's unique serial, LAN MAC address, etc.</p>
<h2 id="flashing-your-device" tabindex="-1"><a class="header-anchor" href="#flashing-your-device"><span>Flashing Your Device</span></a></h2>
<p>Now that everything is prepped, time to flash the device. To be thorough, we'll perform a 2nd verification after flashing to ensure the integrity of the flashed firmware.</p>
<ol>
<li>
<p>Flash the firmware:</p>
<ul>
<li>For Intel-based ChromeOS devices, flash using
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi -w &lt;filename&gt; --ifd -i bios -N</code></li>
</ul>
</li>
<li>For all other devices, use
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi -w &lt;filename&gt;</code></li>
</ul>
</li>
</ul>
<p>Where <code v-pre>&lt;filename&gt;</code> is the name of your backup file, UEFI firmware file, or shellball firmware file. This will usually take 30s-90s to complete; flashrom will first read the flash chip, determine which sectors differ, erase those sectors, write the new data, then verify the data written.</p>
</li>
<li>
<p>Verify the firmware</p>
<p>Even though flashrom does this as part of the write process, verifying the entire flash chip is quick and an easy way to ensure everything went as it should:</p>
<ul>
<li>As before, if flashing an Intel-based device, use
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi -v &lt;filename&gt; --ifd -i bios</code></li>
</ul>
</li>
<li>Otherwise, use
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi -v &lt;filename&gt;</code></li>
</ul>
</li>
</ul>
<p>Using the same filename as before. If the verification passes, then disconnect the CH341a from the host machine, and then remove the chip clip.</p>
</li>
<li>
<p>Disable software write-protect and clear wp-range (UEFI first-time flash only)</p>
<p>This step is only necessary when flashing the UEFI Full ROM firmware on a device running stock firmware which has not previously been flashed. It is necessary for the UEFI firmware to boot properly. Run the following commands:</p>
<ul>
<li><code v-pre>sudo flashrom -p ch341a_spi --wp-disable</code></li>
<li><code v-pre>sudo flashrom -p ch341a_spi --wp-range 0,0</code></li>
</ul>
</li>
</ol>
<h3 id="clean-up" tabindex="-1"><a class="header-anchor" href="#clean-up"><span>Clean Up</span></a></h3>
<p>Reassembly is the reverse of disassembly. Reconnect the internal battery and replace the bottom cover/keyboard. Flip over the device, connect external power, press the power button, and cross your fingers 😃</p>
</div></template>
<script>
export const _pageData = JSON.parse("{\"path\":\"/docs/support/unbricking/unbrick-ch341a.html\",\"title\":\"Unbricking/Flashing with a ch341a USB Programmer\",\"lang\":\"en-US\",\"frontmatter\":{},\"git\":{\"updatedTime\":1789618099000,\"contributors\":[{\"name\":\"Matt DeVillier\",\"username\":\"\",\"email\":\"matt.devillier@gmail.com\",\"commits\":12},{\"name\":\"player-k12\",\"username\":\"player-k12\",\"email\":\"160201654+player-k12@users.noreply.github.com\",\"commits\":1,\"url\":\"https://github.com/player-k12\"},{\"name\":\"Piotr Masłowski\",\"username\":\"\",\"email\":\"piotr@maslowski.xyz\",\"commits\":2}],\"changelog\":[{\"hash\":\"0995c1ecb815a5014238ada69ab122d6ead31114\",\"time\":1789618099000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"unbricking: SuzyQ is CR50 and Ti50; battery is not a Ti50 WP fallback\"},{\"hash\":\"ed01220962891706f5dd886c559d1ea892e842da\",\"time\":1784173451000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: document script updates and simplify Full ROM download for unbricking\"},{\"hash\":\"8f5b06ff1af662d9664fda305d12384788fe5d41\",\"time\":1771975723000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"unbricking: Add WP disable commands for first-time flashing\"},{\"hash\":\"7ea4d02bdfa0e3f14244aeb287e8895ee440828b\",\"time\":1764433110000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"unbricking: add UEFI firmware image name override lookup\"},{\"hash\":\"1b081d43d3fd1180de9e0e168df48e6a87893897\",\"time\":1762986515000,\"email\":\"piotr@maslowski.xyz\",\"author\":\"Piotr Masłowski\",\"message\":\"project-wide: Trim trailing whitespace\"},{\"hash\":\"e82a1cc542041a1354c0e1bdeadc47728b32f153\",\"time\":1762984012000,\"email\":\"piotr@maslowski.xyz\",\"author\":\"Piotr Masłowski\",\"message\":\"project-wide: Remove spurious backslashes\"},{\"hash\":\"1261da0575b8516184f9c7bef9f00f014143d204\",\"time\":1761160316000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"Unbricking: Add clarifcation and fix formatting for file types\"},{\"hash\":\"143b3546dd95948ffbfcb9f5dab25f2049eced25\",\"time\":1746377503000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"Update info on how to get a shellball ROM file\"},{\"hash\":\"43ee7b27b52338d542ac22104acadbcff1c91bbd\",\"time\":1728484792000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"Minor tweaks to last PR\"},{\"hash\":\"08186cc87f3b45e101fd5e8b3509fbf7f3fce125\",\"time\":1728484266000,\"email\":\"160201654+player-k12@users.noreply.github.com\",\"author\":\"Ed\",\"message\":\"Update manual-flashing.md  (#21)\"},{\"hash\":\"62c0c0fc2bfe7a43f819445ae8466c4f318a1869\",\"time\":1727535988000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"unbrick-ch341a.md: fix flashing command, formatting\"},{\"hash\":\"b2f87b462f71e7116b336df9b3ad24de0846e4f8\",\"time\":1720805777000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"unbricking: Update flashing commands, clean up formatting\"},{\"hash\":\"31754e9a539127b24596a6ef24c63c25693b74b1\",\"time\":1720622740000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"project-wide: trim all trailing whitespace\"},{\"hash\":\"4438703328f5f644a3eaf7866aa5c7e701c2f651\",\"time\":1720552626000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"Address MD formatting errors detected by linter\"},{\"hash\":\"39a8e8685b2be911b99ae30f8f0c7fb6234b5e50\",\"time\":1719969991000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"WIP content update\"}]},\"filePathRelative\":\"docs/support/unbricking/unbrick-ch341a.md\"}")
export default { name: "unbrick-ch341a.md" }
</script>